/**
 * API facades: readable wrappers around generated services with hashed method names.
 * Use extractList(res) / extractOne(res) to normalize list vs single-object responses.
 */

import { GeneralSettingsService } from './generated/services/GeneralSettingsService';
import { CountriesService } from './generated/services/CountriesService';
import { StatesService } from './generated/services/StatesService';
import { CitiesService } from './generated/services/CitiesService';
import { CaseTypesService } from './generated/services/CaseTypesService';
import { CourtTypesService } from './generated/services/CourtTypesService';
import { CourtsService } from './generated/services/CourtsService';
import { DepartmentsService } from './generated/services/DepartmentsService';
import { ClientsService } from './generated/services/ClientsService';
import { JudgesService } from './generated/services/JudgesService';
import { CourtCasesService } from './generated/services/CourtCasesService';
import { FormsAndPrecedentsService } from './generated/services/FormsAndPrecedentsService';
import { AppointmentsService } from './generated/services/AppointmentsService';
import { ExpensesService } from './generated/services/ExpensesService';
import { InvoicesService } from './generated/services/InvoicesService';
import { AuthenticationService } from './generated/services/AuthenticationService';
import { DocumentSigningService } from './generated/services/DocumentSigningService';
import { axiosInstance } from './axios-instance';

// --- Helpers ---

/** Normalize list responses: Laravel paginator (data.data.data) or direct array (data/data.data) or [] */
export function extractList(res: { data?: any } | null | undefined): any[] {
  const body = res?.data;
  if (!body) return [];
  const inner = body.data;
  if (Array.isArray(inner)) return inner;
  if (inner && typeof inner === 'object' && Array.isArray(inner.data)) return inner.data;
  return [];
}

/** Normalize single-resource: data.data or data */
export function extractOne(res: { data?: any } | null | undefined): any {
  return res?.data?.data ?? res?.data;
}

// --- General Settings ---

export const generalSettingsApi = {
  list: () => GeneralSettingsService.fdf72C73D24231337007E3C7952C6Ad1(),
  createOrUpdate: (body: {
    company_name?: string;
    address?: string;
    country?: number;
    state?: number;
    city?: number;
    pincode?: number;
    date_formet?: number;
    logo_img?: string;
    favicon_img?: string;
    timezone?: string;
  }) => GeneralSettingsService.da2Bec58B4A565B7E3Ff3Cd8C9B0784(body),
  get: (id: number) => GeneralSettingsService.c6892Aafd2E9Ba0577F154B2685204(id),
  update: (
    id: number,
    body: {
      company_name?: string;
      address?: string;
      country?: number;
      state?: number;
      city?: number;
      pincode?: number;
      date_formet?: number;
      logo_img?: string;
      favicon_img?: string;
      timezone?: string;
    }
  ) => GeneralSettingsService.eb3Ad8F421380A29328Ff6735Be33E46(id, body),
};

// --- Countries ---

export const countriesApi = {
  list: (params?: { search?: string; perPage?: number }) =>
    CountriesService.a08Ff99B9Afd86C714C683Cb74C584B8(params?.search, params?.perPage ?? 15),
  get: (id: number) => CountriesService.b650C2Bdb0B69838471965D3F0814B5(id),
};

// --- States ---

export const statesApi = {
  list: (params?: { countryId?: number; search?: string; perPage?: number }) =>
    StatesService.e4A9654897D4E158D47Dbb723A3826(params?.countryId, params?.search, params?.perPage ?? 15),
  get: (id: number) => StatesService.c3Eb441051Bbd63D9876F8611193Be6(id),
};

// --- Cities ---

export const citiesApi = {
  list: (params?: { stateId?: number; search?: string; perPage?: number }) =>
    CitiesService.a98960203C8028B92C060C3Df72D886(params?.stateId, params?.search, params?.perPage ?? 15),
  get: (id: number) => CitiesService.b63E63C5142F261F2B76A2D432679D3(id),
};

// --- Case Types ---

export const caseTypesApi = {
  list: (params?: { parentId?: number; isActive?: 'Yes' | 'No'; search?: string }) =>
    CaseTypesService.f8Bba1D05Cbe34A2790612Fb2884D(params?.parentId, params?.isActive, params?.search),
  create: (body: { parent_id?: number | null; case_type_name: string; is_active?: 'Yes' | 'No' }) =>
    CaseTypesService.a926430B1281A815885B22D745Fa4Afc(body),
  get: (id: number) => CaseTypesService.c50D5685E67Ed5880D645099Aa5Cc06(id),
  update: (id: number, body: { parent_id?: number | null; case_type_name?: string; is_active?: 'Yes' | 'No' }) =>
    CaseTypesService.f881459B45A830A7Fa27Badb67Cafe8C(id, body),
  remove: (id: number) => CaseTypesService.dc94Bed0A85224F2Bdee04536E8462D(id),
};

// --- Court Types ---

export const courtTypesApi = {
  list: (params?: { isActive?: 'Yes' | 'No'; search?: string }) =>
    CourtTypesService.cf59D5654A1Afd1E2B1Ac(params?.isActive, params?.search),
  create: (body: { court_type_name: string; is_active?: 'Yes' | 'No' }) =>
    CourtTypesService.c6C7B735Bc6Bae01F48429F514551(body),
  get: (id: number) => CourtTypesService.a6B922874716097Ae7Cf2E57Ef73D303(id),
  update: (id: number, body: { court_type_name?: string; is_active?: 'Yes' | 'No' }) =>
    CourtTypesService.fd1049203428Dad909A31D347869F0Ed(id, body),
  remove: (id: number) => CourtTypesService.a3443Fcf56Df8E40C51E498293E4B632(id),
};

// --- Courts ---

export const courtsApi = {
  list: (params?: { courtTypeId?: number; isActive?: 'Yes' | 'No'; search?: string }) =>
    CourtsService.ae0F3Cffb80Ac352Ec96485D6095183(params?.courtTypeId, params?.isActive, params?.search),
  create: (body: { court_type_id: number; court_name: string; is_active?: 'Yes' | 'No' }) =>
    CourtsService.c80Df7B043816Cfbee1D634Fa6A2A16E(body),
  get: (id: number) => CourtsService.e084Abdab84E8424Ff023E7F9E2Ea095(id),
  update: (id: number, body: { court_type_id?: number; court_name?: string; is_active?: 'Yes' | 'No' }) =>
    CourtsService.e3882Ca58A4Fc26E29A005E89D2096(id, body),
  remove: (id: number) => CourtsService.ae40E1Abb6443D5519A7716084Da1C(id),
};

// --- Departments ---

export const departmentsApi = {
  list: () => DepartmentsService.e000F92693Bdc7B2C9846C82795(),
  create: (body: {
    department_name: string;
    people_id?: number | null;
    email?: string | null;
    mobile?: string | null;
    description?: string | null;
  }) => DepartmentsService.d4635Ae7B4C15Efca40765102F9Ff8D6(body),
  get: (id: number) => DepartmentsService.c2D12E14Cd820Aa51B5Ccdc59C29Eb(id),
  update: (id: number, body: { department_name?: string; is_active?: 'Yes' | 'No' }) =>
    DepartmentsService.cbc0A492F9379Edab60B247E7D04A7Ae(id, body),
  remove: (id: number) => DepartmentsService.f2Ed74585Edcde1C2F968Caa9E9B2(id),
};

// --- Clients (advocate-clients) ---

export type ClientCreateBody = {
  name: string;
  company_name?: string | null;
  people_id?: number | null;
  first_name?: string;
  last_name?: string | null;
  email?: string | null;
  mobile?: string | null;
  address?: string | null;
  client_type: 'single' | 'multiple';
  reference_name?: string | null;
  reference_mobile?: string | null;
  main_contact_position?: string | null;
  company_address?: string | null;
};

export type ClientUpdateBody = {
  name?: string;
  company_name?: string | null;
  first_name?: string;
  last_name?: string | null;
  email?: string | null;
  mobile?: string | null;
  address?: string | null;
  main_contact_position?: string | null;
  company_address?: string | null;
  client_type?: 'single' | 'multiple';
  is_active?: 'Yes' | 'No';
};

export const clientsApi = {
  list: (params?: { isActive?: 'Yes' | 'No'; clientType?: 'single' | 'multiple'; search?: string }) =>
    ClientsService.e670D7D89A727399362952De764670F(params?.isActive, params?.clientType, params?.search),
  create: (body: ClientCreateBody) =>
    ClientsService.d277802F6Aab301945Ea50B60B15Dda(body as any),
  get: (id: number) => ClientsService.a4606F1D8605B3C834Fecfb920E9565(id),
  update: (id: number, body: ClientUpdateBody) =>
    ClientsService.ead132E58B447B79Af61732D0298B(id, body as any),
  remove: (id: number) => ClientsService.bc860Ba00514332C1100E3E18D51B35(id),
};

// --- Case Statuses ---

export const caseStatusesApi = {
  list: () => axiosInstance.get<{ success?: boolean; data?: Array<{ id: number; case_status_name?: string }> }>('/case-statuses'),
};

// --- Judges ---

export const judgesApi = {
  list: (params?: { isActive?: 'Yes' | 'No'; search?: string }) =>
    JudgesService.be3F674Dae85168Df163Bf6Ccbfb(params?.isActive, params?.search),
  create: (body: { judge_name: string; is_active?: 'Yes' | 'No' }) =>
    JudgesService.b7933C4965F87A0Ea979E12(body),
  get: (id: number) => JudgesService.e70647F5049942839482383543A3304C(id),
  update: (id: number, body: { judge_name?: string; is_active?: 'Yes' | 'No' }) =>
    JudgesService.b041Bbd58791Bd33043Ae69C23B9Fa9F(id, body),
  remove: (id: number) => JudgesService.a1D0A4C7Acc0B98C564A9C1E5E5A265F(id),
};

// --- Court Cases ---

export const courtCasesApi = {
  list: (params?: {
    clientId?: number;
    caseStatus?: number;
    priority?: 'High' | 'Medium' | 'Low';
    search?: string;
    perPage?: number;
    dateFrom?: string;
    dateTo?: string;
  }) =>
    axiosInstance.get<{ success?: boolean; data?: any }>('/court-cases', {
      params: {
        client_id: params?.clientId,
        case_status: params?.caseStatus,
        priority: params?.priority,
        search: params?.search,
        per_page: params?.perPage ?? 15,
        date_from: params?.dateFrom,
        date_to: params?.dateTo,
      },
    }),
  create: (body: {
    advo_client_id: number;
    department_id?: number | null;
    client_position: 'Respondent' | 'Petitioner';
    party_name: string;
    party_lawyer: string;
    case_number?: string | null;
    case_types: number;
    case_sub_type?: number | null;
    case_status: number;
    priority: 'High' | 'Medium' | 'Low';
    court_type: number;
    court: number;
    judge_type: number;
    remark?: string | null;
    description?: string | null;
    date_of_service?: string | null;
  }) => CourtCasesService.dbaf97C4E9Bd8Cce8Bd5E8097A3D295F(body as any),
  get: (id: number) => CourtCasesService.a8444F144433Ad212Ca9E1Dceced96E(id),
  update: (
    id: number,
    body: {
      advo_client_id?: number;
      case_status?: number;
      priority?: 'High' | 'Medium' | 'Low';
      is_active?: 'Yes' | 'No';
    }
  ) => CourtCasesService.afa5674Bb49Ce35C856Cd28A8B8Eed6(id, body),
  remove: (id: number) => CourtCasesService.b04Ffdb8D512E02705C1Fa5Fd87913Ed(id),
};

// --- Workflows ---

export const workflowsApi = {
  list: (params?: { is_active?: string; per_page?: number }) =>
    axiosInstance.get<{ success?: boolean; data?: any }>('/workflows', { params }),
  get: (id: number) => axiosInstance.get<{ success?: boolean; data?: any }>(`/workflows/${id}`),
  create: (body: { name: string; description?: string | null; is_active?: string }) =>
    axiosInstance.post<{ success?: boolean; data?: any }>('/workflows', body),
  update: (id: number, body: { name?: string; description?: string | null; is_active?: string }) =>
    axiosInstance.put<{ success?: boolean; data?: any }>(`/workflows/${id}`, body),
  remove: (id: number) => axiosInstance.delete<{ success?: boolean }>(`/workflows/${id}`),
  getVisualization: (id: number) =>
    axiosInstance.get<{ success?: boolean; data?: { nodes: any[]; edges: any[]; workflow_name?: string } }>(`/workflows/${id}/visualization`),
};

// --- Tasks ---

export const tasksApi = {
  list: (params?: { workflow_id?: number; per_page?: number; date_from?: string; date_to?: string }) =>
    axiosInstance.get<{ success?: boolean; data?: any }>('/tasks', { params }),
  get: (id: number) => axiosInstance.get<{ success?: boolean; data?: any }>(`/tasks/${id}`),
  create: (body: {
    workflow_id?: number | null;
    order?: number | null;
    depends_on_task_id?: number | null;
    task_subject: string;
    project_status?: string | null;
    priority?: string | null;
    start_date?: string | null;
    end_date?: string | null;
    description?: string | null;
    user_ids?: number[];
  }) => axiosInstance.post<{ success?: boolean; data?: any }>('/tasks', body),
  update: (id: number, body: {
    workflow_id?: number | null;
    order?: number | null;
    depends_on_task_id?: number | null;
    task_subject?: string;
    project_status?: string | null;
    priority?: string | null;
    start_date?: string | null;
    end_date?: string | null;
    description?: string | null;
    user_ids?: number[];
  }) => axiosInstance.put<{ success?: boolean; data?: any }>(`/tasks/${id}`, body),
  remove: (id: number) => axiosInstance.delete<{ success?: boolean }>(`/tasks/${id}`),
};

// --- Users (for assignee dropdowns) ---

export const usersApi = {
  list: (params?: { search?: string; per_page?: number }) =>
    axiosInstance.get<{ success?: boolean; data?: any }>('/users', { params }),
};

// --- Forms and Precedents ---

export const formsAndPrecedentsApi = {
  list: (params?: { search?: string; perPage?: number }) =>
    FormsAndPrecedentsService.cb51Be626D280480Caf0Fe63F5F85302(params?.search, params?.perPage ?? 15),
  create: (body: { name?: string; document_name?: string; keywords?: string; description?: string }) =>
    FormsAndPrecedentsService.dca7Fc122768D2E333Aa05F588E4485(body),
  get: (id: number) => FormsAndPrecedentsService.a36A7C68770F44Bd64C801Ed16712(id),
  update: (
    id: number,
    body: { name?: string; document_name?: string; keywords?: string; description?: string }
  ) => FormsAndPrecedentsService.d470A2583F555Cffba24F0101444C45(id, body),
  remove: (id: number) => FormsAndPrecedentsService.af20Fd97D11Eb5891A536523F6724(id),
};

// --- Appointments ---

export const appointmentsApi = {
  list: (params?: { clientId?: number; dateFrom?: string; dateTo?: string }) =>
    axiosInstance.get<{ success?: boolean; data?: any }>('/appointments', {
      params: {
        client_id: params?.clientId,
        date_from: params?.dateFrom,
        date_to: params?.dateTo,
      },
    }),
  create: (body: {
    client_id: number;
    department_id?: number | null;
    type: string;
    date: string;
    time: string;
    name: string;
    mobile?: string | null;
    note?: string | null;
  }) => AppointmentsService.ad67700Abe95E180Abd8D79890Eaa7B8(body),
  get: (id: number) => AppointmentsService.abb6Faa60A95A5C5293F3C4D2B3248(id),
  update: (id: number, body: { client_id?: number; date?: string; time?: string; is_active?: 'Yes' | 'No' }) =>
    AppointmentsService.af74D8A4523B36Eb424D4B4D1463Cc9(id, body),
  remove: (id: number) => AppointmentsService.f66861F831376E0Db79C07F3(id),
};

// --- Expenses ---

export const expensesApi = {
  list: (params?: {
    year?: number;
    departmentId?: number;
  }) =>
    axiosInstance.get<{ success?: boolean; data?: any }>('/expenses', {
      params: {
        year: params?.year,
        department_id: params?.departmentId,
      },
    }),
  create: (body: {
    vendor_id?: number;
    name: string;
    description?: string | null;
    department_id?: number | null;
    invoice_no: string;
    sub_total_amount: number;
    tax_amount?: number | null;
    inv_date: string;
  }) =>
    axiosInstance.post<{ success?: boolean; message?: string; data?: any }>(
      '/expenses',
      {
        ...body,
        remarks: body.description,
      } as any,
    ),
  get: (id: number) =>
    axiosInstance.get<{ success?: boolean; data?: any }>(`/expenses/${id}`),
  update: (id: number, body: {
    name?: string;
    description?: string | null;
    sub_total_amount?: number;
    tax_amount?: number | null;
    department_id?: number | null;
    inv_status?: string;
    inv_date?: string;
    is_active?: 'Yes' | 'No';
  }) =>
    axiosInstance.put<{ success?: boolean; message?: string; data?: any }>(
      `/expenses/${id}`,
      {
        ...body,
        remarks: body.description,
      } as any,
    ),
  remove: (id: number) => ExpensesService.c26749846Eda472Abe88D46C3823F030(id),
};

// --- Budgets ---

export const budgetsApi = {
  list: (params?: { year?: number; departmentId?: number; isActive?: 'Yes' | 'No'; perPage?: number }) =>
    axiosInstance.get<{ success?: boolean; data?: any }>('/budgets', {
      params: {
        year: params?.year,
        department_id: params?.departmentId,
        is_active: params?.isActive,
        per_page: params?.perPage ?? 15,
      },
    }),
  get: (id: number) =>
    axiosInstance.get<{ success?: boolean; data?: any }>(`/budgets/${id}`),
  create: (body: {
    year: number;
    ceiling_amount: number;
    department_id?: number | null;
    category?: string | null;
    description?: string | null;
    is_active?: 'Yes' | 'No';
  }) =>
    axiosInstance.post<{ success?: boolean; message?: string; data?: any }>(
      '/budgets',
      body,
    ),
};

// --- Invoices ---

export const INVOICE_STATUS_OPTIONS = ['Due', 'Partially Paid', 'Paid'] as const;
export type InvoiceStatus = (typeof INVOICE_STATUS_OPTIONS)[number];

export const invoicesApi = {
  list: (params?: { clientId?: number; invStatus?: InvoiceStatus; perPage?: number }) =>
    axiosInstance.get<{ success?: boolean; data?: any }>('/invoices', {
      params: {
        client_id: params?.clientId,
        inv_status: params?.invStatus,
        per_page: params?.perPage ?? 15,
      },
    }),
  create: (body: {
    client_id: number;
    invoice_no: string;
    inv_date: string;
    due_date: string;
    inv_status: InvoiceStatus;
    remarks?: string | null;
    items: Array<{
      description?: string;
      qty: number;
      rate: number;
      tax?: number | null;
    }>;
  }) =>
    axiosInstance.post<{ success?: boolean; message?: string; data?: any }>(
      '/invoices',
      body,
    ),
  get: (id: number) =>
    axiosInstance.get<{ success?: boolean; data?: any }>(`/invoices/${id}`),
  update: (id: number, body: {
    client_id?: number;
    invoice_no?: string;
    inv_date?: string;
    due_date?: string;
    inv_status?: InvoiceStatus;
    remarks?: string | null;
    items?: Array<{
      description?: string;
      qty: number;
      rate: number;
      tax?: number | null;
    }>;
    is_active?: 'Yes' | 'No';
  }) =>
    axiosInstance.put<{ success?: boolean; message?: string; data?: any }>(
      `/invoices/${id}`,
      body,
    ),
  remove: (id: number) => axiosInstance.delete<{ success?: boolean }>(`/invoices/${id}`),
  downloadPdf: (id: number) =>
    axiosInstance.get<Blob>(`/invoices/${id}/pdf`, { responseType: 'blob' }),
};

// --- Invoice Settings ---

export const invoiceSettingsApi = {
  current: () =>
    axiosInstance.get<{ success?: boolean; data?: { prefix?: string | null; next_invoice_no?: string; client_note?: string | null; term_condition?: string | null } }>(
      '/invoice-settings/current',
    ),
};

// --- User Notification Settings ---

export const userNotificationSettingsApi = {
  get: () =>
    axiosInstance.get<{ success?: boolean; data?: { calendar_deadlines_enabled?: boolean } }>(
      '/user-notification-settings',
    ),
  save: (body: { calendar_deadlines_enabled: boolean }) =>
    axiosInstance.post<{ success?: boolean; message?: string; data?: any }>(
      '/user-notification-settings',
      body,
    ),
};

// --- Documents (upload for a case) ---

export const documentsApi = {
  /** Upload a file as a case document. */
  upload: (caseId: number, file: File, documentName?: string): Promise<{ data?: any }> => {
    const form = new FormData();
    form.append('case_id', String(caseId));
    form.append('file', file);
    if (documentName) form.append('document_name', documentName);
    return axiosInstance.post('/documents', form);
  },
};

// --- Document Library ---

export const documentLibraryApi = {
  getRoots: () =>
    axiosInstance.get<{ success?: boolean; data?: { private_root?: any; shared_root?: any } }>(
      '/document-library/roots',
    ),
  getFolderContents: (folderId: number) =>
    axiosInstance.get<{ success?: boolean; data?: { folder: any; folders: any[]; files: any[] } }>(
      `/document-library/folders/${folderId}/contents`,
    ),
  createFolder: (parentId: number, name: string) =>
    axiosInstance.post<{ success?: boolean; message?: string; data?: any }>(
      '/document-library/folders',
      { parent_id: parentId, name },
    ),
  uploadFile: (folderId: number, file: File, name?: string) => {
    const form = new FormData();
    form.append('file', file);
    if (name) form.append('name', name);
    return axiosInstance.post<{ success?: boolean; message?: string; data?: any }>(
      `/document-library/folders/${folderId}/upload`,
      form,
    );
  },
  renameFolder: (id: number, name: string) =>
    axiosInstance.patch<{ success?: boolean; message?: string; data?: any }>(
      `/document-library/folders/${id}`,
      { name },
    ),
  deleteFolder: (id: number) =>
    axiosInstance.delete<{ success?: boolean; message?: string }>(
      `/document-library/folders/${id}`,
    ),
  renameFile: (id: number, name: string) =>
    axiosInstance.patch<{ success?: boolean; message?: string; data?: any }>(
      `/document-library/files/${id}`,
      { name },
    ),
  deleteFile: (id: number) =>
    axiosInstance.delete<{ success?: boolean; message?: string }>(
      `/document-library/files/${id}`,
    ),
  getFile: (id: number) =>
    axiosInstance.get<{ success?: boolean; data?: any }>(`/document-library/files/${id}`),
  downloadFile: (id: number) =>
    axiosInstance.get<Blob>(`/document-library/files/${id}/download`, {
      responseType: 'blob',
    }),
};

// --- Document Signing ---

export const documentSigningApi = {
  getSigningContext: (documentId: number) => DocumentSigningService.getSigningContext(documentId),
  submitSignature: (documentId: number, body: { signature_data: string; status?: string }) =>
    DocumentSigningService.submitSignature(documentId, body),
  listSignatures: (documentId: number) => DocumentSigningService.listSignatures(documentId),
  getSignature: (documentId: number, signatureId: number) =>
    DocumentSigningService.getSignature(documentId, signatureId),
  downloadSignedPdf: (documentId: number) => DocumentSigningService.downloadSignedPdf(documentId),
};

// --- Authentication ---

export const authApi = {
  me: () => AuthenticationService.e339E0E74860C0647Fd1D41Bbbbc13B2(),
  login: (body: { email: string; password: string }) =>
    AuthenticationService.cd5203E2D0A820E113F2F4Fdb2847(body),
  refresh: () => AuthenticationService.c7Bb98658Eb98880114621321Bd8D8(),
  logout: () => AuthenticationService.a05022A34Eef599E04Fb0A5254375B84(),

  updateProfile: (body: { name: string }) =>
    axiosInstance.put<{ success?: boolean; data?: any }>('/auth/profile', body),

  requestPasswordReset: (body: { email: string }) =>
    axiosInstance.post<{ success?: boolean; message?: string }>(
      '/auth/password/email',
      body,
    ),

  resetPassword: (body: {
    token: string;
    email: string;
    password: string;
    password_confirmation: string;
  }) =>
    axiosInstance.post<{ success?: boolean; data?: any; message?: string }>(
      '/auth/password/reset',
      body,
    ),

  changePassword: (body: {
    current_password: string;
    new_password: string;
    new_password_confirmation: string;
  }) =>
    axiosInstance.put<{ success?: boolean; data?: any; message?: string }>(
      '/auth/password/change',
      body,
    ),
};

// --- Public trial registration ---

export const organizationsPublicApi = {
  search: (params: { query: string; limit?: number }) =>
    axiosInstance.get<{
      success?: boolean;
      data?: Array<{ id: number; name: string; slug?: string }>;
      meta?: { exact_match?: boolean; create_new?: boolean };
    }>('/organizations/search', {
      params: {
        query: params.query,
        limit: params.limit ?? 8,
      },
    }),
};

export const trialRegistrationsApi = {
  create: (body: {
    name: string;
    email: string;
    phone: string;
    organization_id?: number;
    organization_name?: string;
  }) =>
    axiosInstance.post<{
      success?: boolean;
      message?: string;
      data?: any;
      meta?: { email_sent?: boolean };
    }>('/trial-registrations', body),
};

// --- Companion (AnythingLLM) ---

export const companionApi = {
  ssoUrl: () =>
    axiosInstance.get<{ success?: boolean; data?: { url?: string }; message?: string }>(
      '/companion/sso-url',
    ),
  link: () =>
    axiosInstance.post<{ success?: boolean; data?: { companion_id?: number }; message?: string }>(
      '/companion/link',
    ),
};

// --- Mail / SMTP setup ---

export const mailSetupApi = {
  get: () =>
    axiosInstance.get<{
      success?: boolean;
      data?: {
        mail_host?: string;
        mail_port?: string;
        mail_username?: string;
        mail_email?: string;
        mail_password?: string;
        mail_driver?: string;
        mail_encryption?: string;
        mail_from_name?: string;
      };
    }>('/mail-setup'),
  save: (body: {
    mail_host: string;
    mail_port: string;
    mail_username?: string;
    mail_email: string;
    mail_password?: string;
    mail_driver?: string;
    mail_encryption?: string;
    mail_from_name?: string;
  }) =>
    axiosInstance.post<{ success?: boolean; message?: string; data?: any }>(
      '/mail-setup',
      body,
    ),
};

// --- Analytics integration settings (server-side capture) ---

export const analyticsSettingsApi = {
  get: () =>
    axiosInstance.get<{
      success?: boolean;
      data?: {
        base_url?: string;
        system_id?: string;
        api_key_present?: boolean;
        updated_at?: string | null;
      };
    }>('/analytics-settings'),
  save: (body: { base_url?: string | null; system_id?: string | null; api_key?: string | null }) =>
    axiosInstance.post<{ success?: boolean; message?: string; data?: any }>(
      '/analytics-settings',
      body,
    ),
};

// --- AfroNotifications (external POST /api/v1/notify + Bearer token) ---

export const afroNotificationSettingsApi = {
  get: () =>
    axiosInstance.get<{
      success?: boolean;
      data?: {
        base_url?: string;
        default_channel?: string;
        bearer_token_present?: boolean;
        updated_at?: string | null;
      };
    }>('/afro-notification-settings'),
  save: (body: {
    base_url?: string | null;
    default_channel?: string | null;
    bearer_token?: string | null;
  }) =>
    axiosInstance.post<{ success?: boolean; message?: string; data?: any }>(
      '/afro-notification-settings',
      body,
    ),
  test: (body: { recipient: string; channel?: string | null }) =>
    axiosInstance.post<{
      success?: boolean;
      message?: string;
      data?: { http_status?: number | null; detail?: string | null };
    }>('/afro-notification-settings/test', body),
};

// --- Admin: users, roles, permissions ---

export const adminUsersApi = {
  list: (params?: { search?: string; role?: string; per_page?: number }) =>
    axiosInstance.get<{ success?: boolean; data?: any }>('/admin/users', { params }),
  create: (body: {
    email: string;
    password?: string;
    first_name?: string;
    last_name?: string;
    mobile?: string;
    role_ids?: number[];
    is_active?: 0 | 1;
  }) =>
    axiosInstance.post<{ success?: boolean; message?: string; data?: any }>('/admin/users', body),
  update: (
    id: number,
    body: {
      email?: string;
      password?: string;
      first_name?: string;
      last_name?: string;
      mobile?: string;
      role_ids?: number[];
      is_active?: 0 | 1;
    },
  ) =>
    axiosInstance.put<{ success?: boolean; message?: string; data?: any }>(
      `/admin/users/${id}`,
      body,
    ),
  remove: (id: number) =>
    axiosInstance.delete<{ success?: boolean; message?: string }>(`/admin/users/${id}`),
  sendResetLink: (id: number) =>
    axiosInstance.post<{ success?: boolean; message?: string }>(
      `/admin/users/${id}/password/email`,
      {},
    ),
};

export const rolesApi = {
  list: () => axiosInstance.get<{ success?: boolean; data?: any[] }>('/roles'),
  create: (body: { slug: string; description?: string; is_active?: 'Yes' | 'No'; permission_ids?: number[] }) =>
    axiosInstance.post<{ success?: boolean; message?: string; data?: any }>('/roles', body),
  update: (
    id: number,
    body: { slug?: string; description?: string; is_active?: 'Yes' | 'No'; permission_ids?: number[] },
  ) =>
    axiosInstance.put<{ success?: boolean; message?: string; data?: any }>(`/roles/${id}`, body),
  remove: (id: number) =>
    axiosInstance.delete<{ success?: boolean; message?: string }>(`/roles/${id}`),
};

export const permissionsApi = {
  list: () => axiosInstance.get<{ success?: boolean; data?: any[] }>('/permissions'),
};
